/**
 * ACHEditor
 * ACH content editor that uses iframe for CSS isolation.
 * Preview is shown inside the editor iframe (embed app), not in a separate panel.
 * - Development: Uses blob URL iframe
 * - Production: Uses dedicated content-studio app URL (if VITE_ACH_EMBED_URL is set)
 */

import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import type { ContentItem } from '@ai-content-hub/content-core';
import { IframeBlobWrapper, type IframeBlobWrapperRef, type IframeHeightMode } from './IframeBlobWrapper';
import { fetchContent, extractH5PContentFromData } from '@ai-content-hub/shared-api';
import type { ACHEditorRef } from '../../types/content';
import { useACHTranslations } from './i18n';

export interface ACHEditorProps {
    content?: ContentItem;
    contentId?: string;
    showPreview?: boolean;
    /** When using embed URL, pass true to add hiddenHeader=true so embed hides its header. */
    hiddenHeader?: boolean;
    readOnly?: boolean;
    onChange?: (content: ContentItem) => void;
    onValidationError?: (error: string) => void;
    onLoaded?: () => void;
    // API configuration for embed pages
    apiConfig?: {
        baseUrl?: string;
        getAuthToken?: () => string | null;
    };
    /** Default `content`: height from iframe postMessage. `container`: fill parent height. */
    iframeHeightMode?: IframeHeightMode;
    /** Host app UI language (`lang` query + config defaultLanguage in embed). */
    language?: string;
    /** Tenant ID for staff JWT uploads inside iframe. */
    tenantId?: string;
}

export const ACHEditor = forwardRef<ACHEditorRef, ACHEditorProps>(
    (
        {
            content: contentProp,
            contentId,
            showPreview = false,
            hiddenHeader = false,
            readOnly = false,
            onChange,
            onValidationError,
            onLoaded,
            apiConfig,
            iframeHeightMode = 'content',
            language,
            tenantId,
        },
        ref
    ) => {
        const [content, setContent] = useState<ContentItem | null>(contentProp || null);
        const [isLoading, setIsLoading] = useState(false);
        const [error, setError] = useState<string | null>(null);
        const iframeRef = useRef<IframeBlobWrapperRef>(null);

        const t = useACHTranslations();

        // Get embed URL from environment - memoized since it never changes
        const embedUrl = useMemo(
            () =>
                typeof import.meta !== 'undefined'
                    ? (import.meta.env?.VITE_ACH_EMBED_URL as string | undefined)
                    : undefined,
            []
        );

        // Expose getContent method via ref
        useImperativeHandle(
            ref,
            () => ({
                getContent: () => iframeRef.current?.getContent() || content,
            }),
            [content]
        );

        // Fetch content by ID if needed
        useEffect(() => {
            if (contentProp) {
                setContent(contentProp);
                return;
            }

            if (contentId) {
                setIsLoading(true);
                setError(null);

                fetchContent(contentId)
                    .then((fetchedContent) => {
                        const h5pData = extractH5PContentFromData(fetchedContent.data);
                        if (h5pData) {
                            onValidationError?.('This content is H5P type, not ACH. Use H5PEditor instead.');
                            setError('Invalid content type');
                            return;
                        }

                        const achContent: ContentItem = {
                            id: fetchedContent.id,
                            type: fetchedContent.content_type.slug,
                            data: fetchedContent.data,
                            config: fetchedContent.config || undefined,
                        };

                        setContent(achContent);
                        onLoaded?.();
                    })
                    .catch((err) => {
                        const errorMsg = err.message || 'Failed to load content';
                        setError(errorMsg);
                        onValidationError?.(errorMsg);
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }
        }, [contentId, contentProp]);

        // Handle content change from iframe
        const handleChange = useCallback(
            (updatedContent: ContentItem) => {
                setContent(updatedContent);
                onChange?.(updatedContent);
            },
            [onChange]
        );

        // Handle iframe ready
        const handleReady = useCallback(() => {
            onLoaded?.();
        }, [onLoaded]);

        // Handle iframe error
        const handleError = useCallback(
            (errorMsg: string) => {
                onValidationError?.(errorMsg);
            },
            [onValidationError]
        );

        if (isLoading) {
            return (
                <div
                    className="ach-editor-loading"
                    style={{ padding: '24px', textAlign: 'center' }}
                >
                    {t.loading}
                </div>
            );
        }

        if (error) {
            return (
                <div
                    className="ach-editor-error"
                    style={{ padding: '24px', color: 'hsl(var(--destructive, 347 79% 46%))', textAlign: 'center' }}
                >
                    {error}
                </div>
            );
        }

        if (!content) {
            return (
                <div
                    className="ach-editor-empty"
                    style={{ padding: '24px', textAlign: 'center' }}
                >
                    {t.noContent}
                </div>
            );
        }

        return (
            <div
                className="ach-editor-container ach-editor-panel"
                style={{
                    position: 'relative',
                    width: '100%',
                    minHeight: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <IframeBlobWrapper
                    ref={iframeRef}
                    mode="editor"
                    content={content}
                    config={content.config}
                    embedUrl={embedUrl}
                    readOnly={readOnly}
                    showPreview={showPreview}
                    hiddenHeader={hiddenHeader}
                    contentTypeSlug={content?.type}
                    iframeHeightMode={iframeHeightMode}
                    onChange={handleChange}
                    onReady={handleReady}
                    apiConfig={apiConfig}
                    language={language}
                    tenantId={tenantId}
                    onError={handleError}
                />
                {readOnly && (
                    <div
                        className="ach-editor-readonly-overlay"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'transparent',
                            pointerEvents: 'auto',
                            zIndex: 10,
                        }}
                    />
                )}
            </div>
        );
    }
);

ACHEditor.displayName = 'ACHEditor';

export default ACHEditor;

